// "Export pipeline" on the Pipeline page: loads what the LP update needs
// straight from Supabase, renders utils/pipelineReport.js into a hidden
// iframe and opens the browser's print dialog (Save as PDF). Printing through
// the browser keeps the real fonts, vector text and the brand layout exactly
// as designed, with no PDF library in the bundle.

import { supabase } from "../supabase";
import { ACTIVE_STAGES } from "./homeBrief";
import { buildReport, renderReportHtml } from "../utils/pipelineReport";
import fullRed from "../assets/logos/full-red.png";
import iconWhite from "../assets/logos/icon-white.png";

const ROLLING_DAYS = 30;
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseAddresses = (raw) => {
  if (Array.isArray(raw)) return raw;
  try { const v = JSON.parse(raw || "[]"); return Array.isArray(v) ? v : [String(v)]; } catch { return raw ? [String(raw)] : []; }
};
const must = ({ data, error }, what) => {
  if (error) throw new Error(`Could not load ${what}: ${error.message}`);
  return data || [];
};

export async function loadPipelineReport(today = new Date()) {
  const since = new Date(today.getFullYear(), today.getMonth(), today.getDate() - ROLLING_DAYS);
  const lo = localIso(since);
  const hi = localIso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  const notOutreach = (r) => r.deal_flow !== "outreach";

  const [active, entered, killed, lois] = await Promise.all([
    supabase.from("deals")
      .select("deal_name,source_notes,deal_stage,deal_flow,address,geocode_matched,market,square_footage,underwritten_price,marketed,broker_firm,call_for_offers_date,created_at")
      .in("deal_stage", ACTIVE_STAGES),
    supabase.from("deals").select("deal_stage,deal_flow,market,square_footage").gte("created_at", lo).lt("created_at", hi),
    supabase.from("deals").select("deal_stage,killed_reason").gte("killed_at", lo).lt("killed_at", hi),
    supabase.from("activity_log").select("entity_id").eq("action", "stage_changed")
      .eq("metadata->>new_stage", "LOI Submitted").gte("created_at", lo).lt("created_at", hi),
  ]);

  return buildReport({
    today, since,
    active: must(active, "active deals").filter(notOutreach).map((r) => ({ ...r, addresses: parseAddresses(r.address) })),
    entered: must(entered, "new deals").filter(notOutreach),
    killed: must(killed, "passed deals"),
    loiDealIds: must(lois, "LOI history").map((e) => e.entity_id),
  });
}

/** Render the report and open the print dialog. Resolves once the dialog has been opened. */
export async function exportPipelineReport() {
  const report = await loadPipelineReport();
  const abs = (src) => new URL(src, window.location.href).href;
  const html = renderReportHtml(report, { logo: abs(fullRed), mark: abs(iconWhite) });

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0", visibility: "hidden" });
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  doc.open();
  doc.write(html);
  doc.close();

  // Print only once the logo and Montserrat are in, or the PDF falls back to Arial.
  await Promise.all([...doc.images].map((img) => (img.complete ? null : new Promise((ok) => { img.onload = img.onerror = ok; }))));
  await Promise.race([doc.fonts?.ready, new Promise((ok) => setTimeout(ok, 4000))]);

  const title = document.title;
  document.title = doc.title;              // Chrome names the PDF after the top document's title
  const cleanup = () => { document.title = title; frame.remove(); };
  frame.contentWindow.addEventListener("afterprint", () => setTimeout(cleanup, 0), { once: true });
  setTimeout(() => { if (frame.isConnected) cleanup(); }, 120000);
  frame.contentWindow.focus();
  frame.contentWindow.print();
}
